const HERO_IMAGE =
  'https://images.unsplash.com/photo-1530549387789-4c1017266635?auto=format&fit=crop&w=1600&q=80';

/** Featured guide: mile swim practice tips with free YouTube technique videos */
export const SWIM_MILE_PRACTICE_POST = {
  id: 'featured-swim-mile-practice',
  slug: 'swim-mile-practice-tips-technique-videos',
  title: 'How to Train for a Mile Swim: Practice Tips + Free Technique Videos',
  excerpt:
    'Build up to 1,600 m (one mile) with a 4-week pool progression, open-water skills, and hand-picked YouTube sessions on freestyle, breathing, sighting, and drills. Not medical advice.',
  publishedAt: '2026-07-23T11:30:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Endurance Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Keep building your swim',
  relatedPosts: [
    {
      slug: 'triathlon-beginner-training-plan-one-month-before-race',
      title: 'Beginner Triathlon Plan (300 m · 7 mi · 1.5 mi)',
      blurb: 'fit a mile swim into your first race prep.',
    },
    {
      slug: 'triathlon-sprint-training-plan-one-month-before-race',
      title: 'Sprint Triathlon Plan (750 m · 13 mi · 5K)',
      blurb: 'when you are ready to race sprint distance.',
    },
    {
      slug: 'best-triathlon-bikes-value-guide-2026',
      title: 'Best Triathlon Bikes: Value Picks for 2026',
      blurb: 'gear up for the bike leg after you nail the swim.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '1,600 m', label: 'One mile in the pool' },
        { value: '3×', label: 'Swims per week' },
        { value: '4 weeks', label: 'Build-up block' },
        { value: '12+', label: 'Free video links' },
      ],
    },
    {
      type: 'panel',
      title: 'What “a mile” means',
      paragraphs: [
        'In a standard 25-yard pool, one mile is 1,760 yards (70 lengths). In a 25-meter pool, a metric mile is 1,600 m (64 lengths). Triathlon and masters events often use 1,500 m or 1,650 yards—this plan builds toward 1,600 m continuous freestyle with good form.',
        'You should be able to swim 400 m with short rest before starting. Get medical clearance if you have heart, lung, or shoulder issues.',
      ],
      chips: ['Freestyle focus', 'Pool + open water', 'Triathlon friendly', 'Free YouTube'],
    },
    {
      type: 'panel',
      title: 'Top 10 practice tips for mile swimming',
      list: [
        'Exhale underwater—blow bubbles steadily so you only inhale when your mouth clears the surface.',
        'Keep a long, horizontal body line; hips near the surface beats kicking harder.',
        'Use a two-beat or four-beat kick for distance—save your legs for the bike and run if you are training for triathlon.',
        'Breathe bilaterally in practice (every 3 or 5 strokes) so you can switch sides in open water.',
        'Sight every 6–10 strokes in open water: lift eyes only, do not lift your whole head.',
        'Start every session 10% slower than you think you should—negative splits beat blowing up at 400 m.',
        'Use a pull buoy once per week to build arm endurance without over-kicking.',
        'Count strokes per length (SPL)—if SPL rises late in a set, your form is breaking down; ease off.',
        'Practice flip turns or open turns so you do not lose rhythm at walls.',
        'End each week with one continuous swim that is 100–200 m longer than the prior week until you hit 1,600 m.',
      ],
    },
    {
      type: 'table',
      title: '4-week build to one mile (pool)',
      intro:
        'Three swims per week. RPE: easy = full sentences; moderate = 3–4 words between breaths; hard = short repeats only.',
      headers: ['Week', 'Session A', 'Session B', 'Session C'],
      rows: [
        [
          '1',
          'Technique 30 min: drills + 4 × 100 m easy',
          'Endurance 35 min: 6 × 150 m, 20 sec rest',
          'Continuous 30 min: 3 × 200 m steady',
        ],
        [
          '2',
          'Drills 30 min: catch-up + fingertip drag',
          'Endurance 40 min: 4 × 250 m, 25 sec rest',
          'Continuous 35 min: 800 m (stop 30 sec every 400 m if needed)',
        ],
        [
          '3',
          'Speed 35 min: 8 × 50 m moderate, 8 × 25 m easy',
          'Endurance 45 min: 3 × 400 m, 30 sec rest',
          'Continuous 40 min: 1,000 m non-stop',
        ],
        [
          '4',
          'Technique 25 min: sighting + bilateral breathing',
          'Endurance 40 min: 2 × 600 m, 45 sec rest',
          'Mile day: 1,600 m continuous (or 70 × 25 yd with 10 sec rest as stepping stone)',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Key drills to use every week',
      list: [
        'Catch-up drill: one arm extended until the other catches up—builds rhythm and front-quadrant timing.',
        'Fingertip drag: drag fingertips along the surface on recovery—promotes high elbow.',
        '6-1-6 (or 6-3-6): six kicks on side, one stroke, switch—body position and rotation.',
        'Single-arm freestyle: one arm only, other extended—balance and catch feel.',
        'Tarzan / sighting drill: head up for 4–6 strokes mid-pool—open-water navigation practice.',
        'Pull buoy + paddles (optional): 4 × 200 m steady—arm endurance for the second half of a mile.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — freestyle basics & body position',
      videos: [
        {
          title: 'Learn To Swim Freestyle | A Simple Step-By-Step Guide',
          channel: 'Global Triathlon Network (GTN)',
          description:
            'Full beginner breakdown: entry, catch, pull, recovery, kick, rotation, and breathing—start here if freestyle is new.',
          url: 'https://www.youtube.com/watch?v=6_vXycbD2TM',
        },
        {
          title: 'The Basics Of Freestyle Swimming | GTN Swim Week',
          channel: 'Global Triathlon Network (GTN)',
          description: 'Back-to-basics episode for triathletes learning their first continuous lengths.',
          url: 'https://www.youtube.com/watch?v=2QLe7k27qOs',
        },
        {
          title: 'How To Get Your Timing Right When Breathing In Freestyle',
          channel: 'Effortless Swimming',
          description:
            'Fix late breathing—hand entry cues head turn; snap back to center when you start the pull.',
          url: 'https://www.youtube.com/watch?v=c28SMt8CraI',
        },
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — drills for a longer, efficient stroke',
      videos: [
        {
          title: '4 Swim Drills That ANYONE Can Do',
          channel: 'Global Triathlon Network (GTN)',
          description:
            'Catch-up, fingertip drag, single-arm, and 6-1-6—four drills that directly support mile pacing.',
          url: 'https://www.youtube.com/watch?v=6dfYvTYxzRY',
        },
        {
          title: 'How To Train for Open Water Swimming',
          channel: 'TENSSWIM / open-water coaching',
          description:
            'Pool-to-open-water progression: sighting, breathing adjustments, and race-day technique.',
          url: 'https://www.youtube.com/watch?v=v9MitspJlhg',
        },
        {
          title: '6 Open Water Swimming Tips Every Beginner Should Know',
          channel: 'Open-water swim coaching',
          description:
            'Bilateral breathing, relaxed kick, and building 30–45 min pool confidence before open water.',
          url: 'https://www.youtube.com/watch?v=8ZJHdiRtG6M',
        },
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — open water & triathlon swim skills',
      videos: [
        {
          title: 'Practice Your Open Water Breathing!',
          channel: 'Global Triathlon Network (GTN)',
          description:
            'Bilateral breathing in chop, slightly higher stroke rate, and adapting to crowded starts.',
          url: 'https://www.youtube.com/watch?v=Ohl_0LixQfM',
        },
      ],
    },
    {
      type: 'panel',
      title: 'Sample 45-minute mile-prep session',
      orderedList: [
        '200 m easy swim + 4 × 50 m drill (catch-up, fingertip drag)',
        '4 × 100 m moderate, 15 sec rest—focus on exhaling underwater',
        'Main set: 3 × 300 m steady, 30 sec rest (add 100 m each week)',
        '4 × 50 m pull buoy, smooth effort',
        '100 m easy cool-down',
      ],
      callout:
        'If you plateau, film one length from the pool deck or use a waterproof watch to check stroke count. Often one technique fix unlocks the next distance jump.',
    },
    {
      type: 'panel',
      title: 'Common mistakes before a mile',
      list: [
        'Going out too fast on the first 200 m—pace the first 400 m like a warm-up.',
        'Holding your breath—creates CO₂ buildup and panic; exhale constantly.',
        'Lifting the head to breathe—rotate hips and shoulders instead.',
        'Sprinting the kick—two-beat kick is enough for distance freestyle.',
        'Skipping easy weeks—mile fitness is built on consistent moderate volume, not one heroic session.',
      ],
    },
  ],
  referenceLinks: [
    {
      label: 'USA Triathlon — swim training resources',
      url: 'https://www.usatriathlon.org/resources/multisport-101',
    },
    {
      label: 'Global Triathlon Network — YouTube channel',
      url: 'https://www.youtube.com/@gtn',
    },
    {
      label: 'Effortless Swimming — technique library',
      url: 'https://www.effortlessswimming.com/',
    },
  ],
};
